import {
  Badge,
  Button,
  Card,
  Divider,
  DropdownButton,
  IconButton,
  Tooltip,
} from '@common/material'
import type { DropdownButtonOption } from '@common/material/DropdownButton/types'
import ConfirmModal from '@common/modal/ConfirmModal'
import { useModal } from '@common/GlobalModal'
import { useMemo } from 'react'
import { RegisterItemInspectionProperty } from '@core/entities/register/RegisterItem/types'
import { ScheduledInspection } from '@core/entities/register/RegisterInspection/ScheduledInspection'
import { RegisterInspection } from '@core/entities/register/RegisterInspection'
import { useUpdateRegisterItemScheduledInspection } from '@core/react-query/features/registers/registers/register/items/item'
import { PageUrls } from '@core/page-urls'
import { localizedStrings } from '@core/strings'
import { Palette } from '@core/theme/color-palette-siteassist'
import { useToast } from '@core/toast'
import { Box, Flex, Text } from '@fundamentals'
import {
  AssignmentTurnedInOutlined,
  EditOutlined,
  MoreHoriz,
  PlayCircleOutline,
  PlaylistAddCheckOutlined,
  Replay,
  StopCircleOutlined,
} from '@mui/icons-material'
import moment from 'moment'
import { useRouter } from 'next/router'
import { RegisterScheduleInspectionsConfigForm } from './RegisterScheduleInspectionsConfigForm'
import { encodeReturnUrl } from 'webapp/utils/returnUrl'
import { RegisterInspectionCardDetailRow } from './RegisterInspectionCardDetailRow'
import { useDrawer } from '@common/GlobalDrawer'

export type InspectionSidePanelTab = 'overview' | 'inspections' | 'tasks'

export type InspectionDetailsClickParams = {
  inspection?: RegisterInspection
  scheduledInspection?: ScheduledInspection
  config: RegisterItemInspectionProperty<string>
  initialTab?: InspectionSidePanelTab
}

export interface RegisterScheduleInspectionsCardProps {
  organisationId: number
  registerId: number
  registerItemId: number
  projectId?: number
  inspectionConfig: RegisterItemInspectionProperty<string>
  scheduledInspection: ScheduledInspection
  /** When true, only Resume is shown (inactive / stopped schedules). */
  isInactive?: boolean
  onInspectionDetailsClick?: (params: InspectionDetailsClickParams) => void
}

export const RegisterScheduleInspectionsCard = ({
  organisationId,
  registerId,
  registerItemId,
  projectId,
  inspectionConfig,
  scheduledInspection,
  isInactive = false,
  onInspectionDetailsClick,
}: RegisterScheduleInspectionsCardProps) => {
  const { showModal, hideModal } = useModal()
  const { hideDrawer } = useDrawer()
  const router = useRouter()
  const toast = useToast()
  const updateScheduledInspection = useUpdateRegisterItemScheduledInspection()

  const checklistTemplates = scheduledInspection.checklistTemplates?.length
    ? scheduledInspection.checklistTemplates
    : []

  const nextDueText = scheduledInspection.nextInspectionDue
    ? moment(scheduledInspection.nextInspectionDue).format('DD MMM YYYY HH:mm')
    : ''

  const isOverdue = scheduledInspection.isOverdue()

  const canSubmitInspection =
    !isInactive && !!projectId && scheduledInspection.canSubmitInspection

  const openEditScheduleForm = () => {
    showModal(
      <RegisterScheduleInspectionsConfigForm
        key={`edit-scheduled-${scheduledInspection.id}`}
        checklistTemplates={checklistTemplates}
        projectId={projectId}
        registerId={registerId}
        registerItemId={registerItemId}
        registerPropertyId={inspectionConfig.registerPropertyId}
        scheduledInspection={scheduledInspection}
      />,
    )
  }

  const runInspection = () => {
    const returnUrl = encodeReturnUrl({
      pathname: router.pathname,
      query: router.query,
    })

    router.push({
      pathname: PageUrls.newChecklistProject({
        organisationId,
        projectId,
      }),
      query: {
        registerId,
        registerItemId,
        projectId,
        registerTemplateInspectionId:
          scheduledInspection.registerTemplateInspectionId,
        inspectionTemplateId:
          scheduledInspection.scheduleConfig?.checklistTemplateId,
        scheduledInspectionId: scheduledInspection.id,
        returnUrl,
        isUsedForInspections: true,
      },
    })
    hideDrawer()
  }

  const handleStopSchedule = () => {
    updateScheduledInspection.mutate(
      {
        registerId,
        registerItemId,
        scheduledInspectionId: scheduledInspection.id,
        data: {
          nextInspectionDue: scheduledInspection.nextInspectionDue,
          scheduleConfig: {
            ...scheduledInspection.scheduleConfig,
            isActive: false,
          },
          assignedTo: scheduledInspection.assignedTo.id,
        },
      },
      {
        onSuccess: () => {
          toast.success(localizedStrings.scheduleStopped)
          hideModal()
        },
        onError: (e) => toast.error(e.message),
      },
    )
  }

  const handleConfirmStop = () => {
    showModal(
      <ConfirmModal onDismiss={hideModal} onConfirm={handleStopSchedule}>
        {localizedStrings.confirmStopSchedule}
      </ConfirmModal>,
    )
  }

  const menuOptions: DropdownButtonOption[] = useMemo(() => {
    if (isInactive) {
      return [
        {
          title: localizedStrings.resume,
          icon: <Replay />,
          onSelect: openEditScheduleForm,
          'data-test': 'resume-scheduled-inspection',
        },
      ]
    }

    const options: DropdownButtonOption[] = [
      {
        title: localizedStrings.editInspection,
        icon: <EditOutlined />,
        onSelect: openEditScheduleForm,
        divider: !canSubmitInspection,
        'data-test': 'edit-scheduled-inspection',
      },
    ]

    if (canSubmitInspection) {
      options.push({
        title: localizedStrings.runInspection,
        icon: <PlayCircleOutline />,
        onSelect: runInspection,
        divider: true,
      })
    }

    if (onInspectionDetailsClick) {
      const viewOptions: DropdownButtonOption[] = []

      if (scheduledInspection.completedRuns > 0) {
        viewOptions.push({
          title: localizedStrings.viewCompletedInspections,
          icon: <PlaylistAddCheckOutlined />,
          onSelect: () => {
            onInspectionDetailsClick({
              scheduledInspection,
              config: inspectionConfig,
              initialTab: 'inspections',
            })
          },
        })
      }

      if (scheduledInspection.openTasks > 0) {
        viewOptions.push({
          title: localizedStrings.viewTask,
          icon: <AssignmentTurnedInOutlined />,
          onSelect: () => {
            onInspectionDetailsClick({
              scheduledInspection,
              config: inspectionConfig,
              initialTab: 'tasks',
            })
          },
        })
      }

      if (viewOptions.length) {
        // Keep the divider before "Stop schedule" on the last visible view option
        viewOptions[viewOptions.length - 1].divider = true
        options.push(...viewOptions)
      }
    }

    options.push({
      title: localizedStrings.stopSchedule,
      icon: <StopCircleOutlined />,
      onSelect: handleConfirmStop,
      'data-test': 'stop-scheduled-inspection',
    })

    return options
  }, [
    isInactive,
    canSubmitInspection,
    onInspectionDetailsClick,
    scheduledInspection,
    inspectionConfig,
  ])

  return (
    <Card
      variant='outlined'
      data-test='scheduled-inspection-card'
      onClick={
        onInspectionDetailsClick
          ? () =>
              onInspectionDetailsClick({
                scheduledInspection,
                config: inspectionConfig,
              })
          : undefined
      }
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 1,
        borderColor: 'divider',
        px: 2,
        py: 1.5,
        '&:hover': {
          borderColor: Palette.Grey[400],
          '& .show-on-hover': { visibility: 'visible' },
        },
        ...(onInspectionDetailsClick ? { cursor: 'pointer' } : {}),
      }}
    >
      <Flex flexDirection='column' gap={1.5}>
        <Flex justifyContent='space-between' alignItems='flex-start' gap={1}>
          <Flex
            alignItems='center'
            gap={0.5}
            sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}
          >
            <Text
              variant='body2'
              fontWeight={500}
              color='text.primary'
              sx={{
                minWidth: 0,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {inspectionConfig.name}
            </Text>
            {scheduledInspection.isCompleted() && (
              <Box
                sx={{
                  flexShrink: 0,
                  color: Palette.Green[700],
                  bgcolor: Palette.Green[50],
                  borderRadius: 1,
                  px: 0.75,
                  py: 0.5,
                  lineHeight: '12px',
                }}
              >
                <Text
                  component='span'
                  variant='body2'
                  sx={{
                    fontSize: '12px',
                    fontWeight: '500',
                    lineHeight: '12px',
                  }}
                >
                  {localizedStrings.completed}
                </Text>
              </Box>
            )}
            {!scheduledInspection.isActive() &&
              !scheduledInspection.isCompleted() && (
                <Box
                  sx={{
                    flexShrink: 0,
                    color: Palette.Grey[700],
                    bgcolor: Palette.Grey[100],
                    borderRadius: 1,
                    px: 0.8,
                    py: 0.1,
                  }}
                >
                  {localizedStrings.inactive}
                </Box>
              )}
          </Flex>
          <Tooltip title={localizedStrings.options} arrow>
            <DropdownButton
              label={localizedStrings.options}
              options={menuOptions}
              renderButton={(onClick) => (
                <IconButton
                  aria-label={localizedStrings.actions}
                  size='small'
                  className='show-on-hover'
                  data-test='scheduled-inspection-card-actions'
                  onClick={(e) => {
                    e.stopPropagation()
                    onClick(e)
                  }}
                  sx={{
                    color: 'text.secondary',
                    mt: -0.5,
                    mr: -1,
                    flexShrink: 0,
                    visibility: 'hidden',
                  }}
                >
                  <MoreHoriz fontSize='small' />
                </IconButton>
              )}
            />
          </Tooltip>
        </Flex>

        <Flex flexDirection='column' gap={1}>
          <RegisterInspectionCardDetailRow
            label={localizedStrings.assignee}
            value={scheduledInspection.assignedTo.getFullName()}
          />
          <RegisterInspectionCardDetailRow
            label={localizedStrings.schedule}
            value={scheduledInspection.getScheduleDisplayLabel()}
          />
          {scheduledInspection.isActive() && (
            <RegisterInspectionCardDetailRow
              label={localizedStrings.nextDue}
              value={
                <Text variant='body2' color={isOverdue ? 'error' : undefined}>
                  {nextDueText}
                </Text>
              }
            />
          )}
          {scheduledInspection.openTasks !== undefined && (
            <RegisterInspectionCardDetailRow
              label={localizedStrings.openTasks}
              value={
                <Badge count={scheduledInspection.openTasks} hideZero={false} />
              }
            />
          )}
        </Flex>

        <Divider sx={{ my: 0 }} />

        <RegisterInspectionCardDetailRow
          label={localizedStrings.completedRuns}
          value={
            <Badge count={scheduledInspection.completedRuns} hideZero={false} />
          }
        />

        {canSubmitInspection ? (
          <Button
            variant='text'
            color='primary'
            size='medium'
            startIcon={<PlayCircleOutline sx={{ fontSize: 20 }} />}
            onClick={runInspection}
            sx={{
              alignSelf: 'flex-start',
              px: 1,
              // this is so the content of the button is aligned with the content of the detail row
              ml: -1,
            }}
          >
            {localizedStrings.runInspection}
          </Button>
        ) : null}
      </Flex>
    </Card>
  )
}
